// src/pages/Login.js

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Form, Button, Alert } from 'react-bootstrap';
import { FaEnvelope, FaLock, FaSignInAlt, FaSpinner } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import brainLogo from '../assets/IMG_0118.png';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, isAuthenticated, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from);
    }
    
    // Check for auth error
    if (authError) {
      setError(authError);
    }
    
    // Check for message from another page (like signup success)
    if (location.state?.message) {
      setError('');  // Clear any existing errors
    }
  }, [isAuthenticated, navigate, location, authError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Form validation
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setLoading(true);
      await login(email, password);
      // Navigate is handled in the useEffect above
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle different Cognito error codes
      if (err.code === 'UserNotFoundException') {
        setError('User not found. Please check your email or sign up.');
      } else if (err.code === 'NotAuthorizedException') {
        setError('Incorrect username or password.');
      } else if (err.code === 'UserNotConfirmedException') {
        setError('Your account is not verified. Please check your email for a verification link.');
      } else {
        setError(err.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="brand-logo-container" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <img 
              src={brainLogo} 
              alt="Brain Logo" 
              style={{ 
                width: '60px', 
                height: '60px',
                filter: 'drop-shadow(0 0 15px rgba(0, 229, 255, 0.6))',
                borderRadius: 'var(--border-radius)',
                border: '2px solid var(--electric-cyan)',
                padding: '6px',
                background: 'linear-gradient(145deg, var(--midnight-steel), var(--shadow-blue))',
                backdropFilter: 'blur(10px)'
              }} 
            />
            <h2 className="login-title">Agent Enterprise</h2>
          </div>
          <p className="login-subtitle">Sign in to continue</p>
        </div>

        <div className="login-body">
          {location.state?.message && (
            <div className="login-success">
              {location.state.message}
            </div>
          )}

          {error && <div className="login-alert">{error}</div>}

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <div className="input-group">
                <span className="input-group-text">
                  <FaEnvelope />
                </span>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={loading}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="input-group">
                <span className="input-group-text">
                  <FaLock />
                </span>
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  disabled={loading}
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="login-btn" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <FaSpinner className="fa-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <FaSignInAlt />
                  Sign In
                </>
              )}
            </button>

            <div className="login-footer">
              <Link to="/reset-password">
                Forgot Password?
              </Link>
              <Link to="/signup">
                Create an Account
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;